import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  FileText, Upload, Sparkles, Check, ArrowRight, Code
} from 'lucide-react';
import PublicHeader from '@/components/PublicHeader';

export default function SmartDocumentParsingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader breadcrumbs={[{ label: 'Features', link: '/how-it-works' }, { label: 'Smart Document Parsing' }]} />

      <div className="pt-32">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-violet-600 to-indigo-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-white">
            <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10" />
            </div>
            <h1 className="text-5xl font-bold mb-4">Smart Document Parsing</h1>
            <p className="text-xl text-violet-100 max-w-3xl mx-auto">
              Automatically extract fields from Word documents in seconds. No manual configuration required.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our intelligent system identifies and extracts template fields automatically
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                1
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Upload className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Upload Your Document</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Simply upload your Word document (.docx) containing placeholders in the format <code className="px-2 py-1 bg-slate-100 rounded text-sm">{'{fieldname}'}</code>
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                2
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Automatic Extraction</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                TwentyDocs scans your document and automatically identifies all fields, extracting them into a structured form builder.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                3
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Check className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Ready to Use</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Your template is instantly ready. Run it with different data sets to generate personalized documents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Field Format */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Supported Field Format</h2>
              <p className="text-lg text-slate-600 mb-6">
                TwentyDocs recognizes placeholders in the standard double-curly-brace format used by many template systems.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Simple Fields</p>
                    <code className="text-sm text-violet-600">{'{client_name}'}, {'{invoice_date}'}, {'{amount}'}</code>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Nested Fields</p>
                    <code className="text-sm text-violet-600">{'{client.full_name}'}, {'{address.city}'}</code>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Date Fields</p>
                    <code className="text-sm text-violet-600">{'{today}'}, {'{contract_date}'}</code>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-2 mb-6">
                <Code className="w-5 h-5 text-violet-400" />
                <span className="text-sm font-mono text-violet-400">example-template.docx</span>
              </div>
              <div className="space-y-3 font-mono text-sm">
                <p className="text-slate-400">// Sample document content:</p>
                <p>Dear <span className="text-yellow-400">{'{client_name}'}</span>,</p>
                <p className="mt-4">Invoice Date: <span className="text-yellow-400">{'{invoice_date}'}</span></p>
                <p>Amount Due: <span className="text-yellow-400">{'{amount}'}</span></p>
                <p className="mt-4">Address: <span className="text-yellow-400">{'{address.street}'}</span></p>
                <p className="ml-16"><span className="text-yellow-400">{'{address.city}'}</span>, <span className="text-yellow-400">{'{address.state}'}</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Key Benefits</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Save time and eliminate errors with automated field extraction
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mb-4">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Zero Configuration</h3>
              <p className="text-sm text-slate-600">No manual field mapping required</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Instant Results</h3>
              <p className="text-sm text-slate-600">Fields extracted in seconds</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-violet-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Preserve Formatting</h3>
              <p className="text-sm text-slate-600">Original document styling maintained</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mb-4">
                <ArrowRight className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Unlimited Fields</h3>
              <p className="text-sm text-slate-600">No limit on field count</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Ready to Get Started?</h2>
          <p className="text-lg text-slate-600 mb-8">
            Try Smart Document Parsing today with our free plan
          </p>
          <Button
            onClick={() => navigate('/auth')}
            size="lg"
            className="h-14 px-10 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-lg font-bold"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
      </div>
    </div>
  );
}
